/**
 * HomePage.jsx — Dashboard / Home page (MUI) — V1-style layout
 */

import { useMemo, useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Paper, Typography, Grid, Chip, Button, Divider, Link } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useJobs } from '../hooks/useJobs';
import { useInvoices } from '../hooks/useInvoices';
import { useTessieStatus } from '../hooks/useTessie';
import { fmtC, fmtCCompact, STATUS_COLORS, toLocalDateStr, snakeToCamel } from '../lib/utils';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { computeHomeMoney } from '../lib/homeMoney';
import { navigateToJob } from '../lib/crossNav';
import { useContactStats } from '../hooks/useContactStats';
import { useRBAC } from '../hooks/useRBAC';
import { useSettings } from '../hooks/useSettings';
import { useFocus } from '../hooks/useFocus';
import WeatherWidget from '../components/weather/WeatherWidget';
import UserPlatePicker from '../components/home/UserPlatePicker';
import UserPlateView from '../components/home/UserPlateView';
import MemeOfTheDay from '../components/home/MemeOfTheDay';
import MoneyBreakdownDialog from '../components/home/MoneyBreakdownDialog';
import HomeTodosCard from '../components/home/HomeTodosCard';

const PLATE_SS_KEY = 'rfos.home.plateUserId';

const ACTIVE_STATUSES = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced'];
const PIPELINE_STAGES = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced', 'paid'];
const STAGE_LABELS = { intake: 'Intake', quoted: 'Quoted', confirmed: 'Confirmed', shooting: 'Shooting', post_production: 'Post Prod', partial_delivery: 'Partial', delivered: 'Delivered', invoiced: 'Invoiced', paid: 'Paid' };

const monoSx = { fontFamily: '"DM Mono", monospace' };

export default function HomePage() {
  const { profile } = useAuth();
  const { data: jobs = [], isLoading: jobsLoading, isError: jobsError } = useJobs();
  const { data: invoices = [], isLoading: invoicesLoading, isError: invoicesError } = useInvoices();
  const { clients: clientCount, isLoading: contactsLoading } = useContactStats();
  const { hasToolAccess, isAdmin } = useRBAC();
  const { isHidden } = useFocus();
  const { data: settings } = useSettings();
  const vehicleAccess = hasToolAccess('vehicle', settings);
  const { data: tessieStatus } = useTessieStatus({ enabled: !!vehicleAccess });
  const navigate = useNavigate();
  const isLoading = jobsLoading || invoicesLoading;
  // Crew-owed sources, admin only. Same keys and mapping as JobsMoneyCounter
  // (raw ledger rows) and useCrewEarnings, so the cache and realtime
  // invalidation are shared.
  const { data: ledgerRaw, isLoading: ledgerLoading, isError: ledgerError } = useQuery({
    queryKey: ['job-earnings-ledger'],
    queryFn: () => api.get('/job-earnings-ledger'),
    enabled: !!isAdmin,
  });
  const { data: crewEarnings, isLoading: crewLoading, isError: crewError } = useQuery({
    queryKey: queryKeys.crewEarnings.all,
    queryFn: async () => (await api.get('/crew-earnings')).map(snakeToCamel),
    enabled: !!isAdmin,
  });
  const [breakdown, setBreakdown] = useState(null); // 'pipeline' | 'booked' | 'outstanding' | 'crew'

  // ── Plate picker (admin only): which user are we viewing? null = company ──
  // sessionStorage so it doesn't persist across days/login sessions
  const [plateUserId, setPlateUserId] = useState(() => {
    if (typeof window === 'undefined') return null;
    try { return sessionStorage.getItem(PLATE_SS_KEY) || null; } catch { return null; }
  });
  useEffect(() => {
    try {
      if (plateUserId) sessionStorage.setItem(PLATE_SS_KEY, plateUserId);
      else sessionStorage.removeItem(PLATE_SS_KEY);
    } catch { /* noop */ }
  }, [plateUserId]);
  // Clear stale selection if user loses admin (e.g. role change)
  useEffect(() => {
    if (!isAdmin && plateUserId) setPlateUserId(null);
  }, [isAdmin, plateUserId]);

  // Live clock
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const todayStr = toLocalDateStr(now);

  // ── V1 stat boxes ──
  const stats = useMemo(() => {
    const activeJobs = jobs.filter((j) => j.status !== 'void' && j.status !== 'archived');
    return { activeJobCount: activeJobs.length };
  }, [jobs, now]);

  // Admin money boxes + their breakdowns. Definitions live in lib/homeMoney.js.
  const money = useMemo(() => {
    if (!isAdmin) return null;
    return computeHomeMoney({
      jobs,
      invoices,
      ledger: (ledgerRaw || []).map(snakeToCamel),
      crewEarnings: crewEarnings || [],
      today: todayStr,
    });
  }, [isAdmin, jobs, invoices, ledgerRaw, crewEarnings, todayStr]);
  // A failed fetch must never render as a confident $0 (useJobs/useInvoices
  // default to []): show '—' and keep the box shut instead.
  const moneyError = jobsError || invoicesError;
  const crewReady = !!ledgerRaw && !!crewEarnings && !ledgerError && !crewError && !moneyError;
  const crewLoadingState = isLoading || ledgerLoading || crewLoading;
  const moneyValue = (n) => (isLoading ? '...' : moneyError ? '—' : fmtC(n));
  const moneySub = (text) => (isLoading || moneyError ? '' : text);

  // ── Today's shoots ──
  const todayJobs = useMemo(() => {
    return jobs.filter((j) => {
      const dates = j.shootDates || (j.shootDate ? [j.shootDate] : []);
      return dates.includes(todayStr) && j.status !== 'archived' && j.status !== 'void';
    });
  }, [jobs, todayStr]);

  // ── Upcoming shoots (next 7 days, excluding today) ──
  const upcomingShoots = useMemo(() => {
    const weekFromNow = toLocalDateStr(new Date(Date.now() + 7 * 86400000));
    return jobs.filter((j) => j.shootDate && j.shootDate > todayStr && j.shootDate <= weekFromNow && ACTIVE_STATUSES.includes(j.status))
      .sort((a, b) => (a.shootDate || '').localeCompare(b.shootDate || ''));
  }, [jobs, todayStr]);

  // ── Action items (V1 style) ──
  // Each item carries a sortDate so the list reads earliest-due first;
  // splits/overdue invoices use today (already actionable) so they bubble up.
  const actionItems = useMemo(() => {
    const items = [];
    const nowMs = Date.now();
    // Splits needed
    jobs.forEach((j) => {
      const dates = j.shootDates || (j.shootDate ? [j.shootDate] : []);
      const lastDate = dates.length ? dates[dates.length - 1] : '';
      if (lastDate && lastDate <= todayStr && !(j.calcSnapshot?.originators || j.calcSnapshot?.teams) && (j.status === 'post_production' || j.status === 'partial_delivery' || j.status === 'delivered'))
        items.push({ icon: '⚡', label: j.title || j.clientName || 'Untitled', sub: 'split needed', jobId: j.id, sortDate: todayStr });
    });
    // Upcoming deliveries (5 days)
    jobs.forEach((j) => {
      if (!j.deliveryDate || j.status === 'delivered' || j.status === 'invoiced' || j.status === 'paid' || j.status === 'archived' || j.status === 'void') return;
      const diff = Math.round((new Date(j.deliveryDate + 'T12:00') - nowMs) / 86400000);
      if (diff >= 0 && diff <= 5)
        items.push({ icon: '📦', label: j.title || j.clientName || 'Untitled', sub: diff === 0 ? 'delivery today' : `delivery in ${diff}d`, jobId: j.id, sortDate: j.deliveryDate });
    });
    // Upcoming shoots (7 days)
    jobs.forEach((j) => {
      if (j.status === 'void' || j.status === 'archived' || j.status === 'paid' || j.status === 'delivered' || j.status === 'invoiced') return;
      const dates = j.shootDates || (j.shootDate ? [j.shootDate] : []);
      const nextDate = dates.filter((sd) => sd > todayStr).sort()[0];
      if (nextDate) {
        const diff = Math.round((new Date(nextDate + 'T12:00') - nowMs) / 86400000);
        if (diff > 0 && diff <= 7)
          items.push({ icon: '📸', label: j.title || j.clientName || 'Untitled', sub: `shoot in ${diff}d`, jobId: j.id, sortDate: nextDate });
      }
    });
    // Overdue invoices
    invoices.forEach((inv) => {
      if ((inv.status === 'unpaid' || inv.status === 'partial') && inv.dueDate && inv.dueDate < todayStr)
        items.push({ icon: '⚠', label: `Invoice #${inv.number || '—'}`, sub: 'overdue', link: `/invoices?select=${inv.id}`, sortDate: inv.dueDate });
    });
    items.sort((a, b) => (a.sortDate || '9999').localeCompare(b.sortDate || '9999'));
    return items.slice(0, 12);
  }, [jobs, invoices, todayStr]);

  // ── Alert strip ──
  const alerts = useMemo(() => {
    const a = [];
    if (todayJobs.length) a.push({ color: '#10B981', msg: `🟢 ${todayJobs.length} shoot${todayJobs.length > 1 ? 's' : ''} scheduled today` });
    const needSplit = jobs.filter((j) => {
      const dates = j.shootDates || (j.shootDate ? [j.shootDate] : []);
      const lastDate = dates.length ? dates[dates.length - 1] : '';
      return lastDate && lastDate <= todayStr && !(j.calcSnapshot?.originators || j.calcSnapshot?.teams) && (j.status === 'post_production' || j.status === 'partial_delivery');
    });
    if (needSplit.length) a.push({ color: '#F59E0B', msg: `⚡ ${needSplit.length} completed shoot${needSplit.length > 1 ? 's' : ''} need a split run` });
    const overdue = jobs.filter((j) => j.deliveryDate && j.deliveryDate < todayStr && j.status !== 'delivered' && j.status !== 'invoiced' && j.status !== 'paid' && j.status !== 'archived' && j.status !== 'void');
    if (overdue.length) a.push({ color: '#dc2626', msg: `🚨 ${overdue.length} overdue deliver${overdue.length > 1 ? 'ies' : 'y'}` });
    const unreviewedDrives = tessieStatus?.unreviewedCount || 0;
    if (unreviewedDrives > 0 && !isHidden('home.drivesPill')) a.push({ color: '#60A5FA', msg: `🚗 ${unreviewedDrives} drive${unreviewedDrives === 1 ? '' : 's'} need review`, link: '/vehicle?tab=drives&confirmed=false', focus: 'home.drivesPill' });
    return a;
  }, [jobs, todayJobs, todayStr, tessieStatus, isHidden]);

  // ── Pipeline data (bar chart) ──
  const pipelineData = useMemo(() => {
    const activeJobs = jobs.filter((j) => j.status !== 'void' && j.status !== 'archived');
    const totalRev = activeJobs.reduce((s, j) => s + (parseFloat(j.revenue) || 0), 0);
    return PIPELINE_STAGES.map((st) => {
      const stJobs = activeJobs.filter((j) => j.status === st);
      const rev = stJobs.reduce((s, j) => s + (parseFloat(j.revenue) || 0), 0);
      const pct = totalRev > 0 ? Math.max(2, Math.round((rev / totalRev) * 100)) : 0;
      return { status: st, count: stJobs.length, rev, pct };
    }).filter((d) => d.count > 0);
  }, [jobs]);

  const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
  const overdueCount = money ? money.outstanding.items.filter((i) => i.daysOverdue > 0).length : 0;
  const statBoxes = [
    { label: 'Active Jobs', value: isLoading ? '...' : String(stats.activeJobCount), color: 'text.primary' },
    { label: 'Clients', value: contactsLoading ? '...' : String(clientCount), color: '#2DD4BF', link: '/people' },
    // Company-wide money is admin-only; crew see counts, not company money.
    // Each box opens MoneyBreakdownDialog with its line items.
    ...(money ? [
      {
        key: 'pipeline', label: 'Pipeline', color: '#13B5EA', disabled: moneyError,
        value: moneyValue(money.pipeline.total),
        sub: moneySub(`${plural(money.pipeline.items.length, 'job', 'jobs')} · not confirmed`),
      },
      {
        key: 'booked', label: 'Booked', color: '#10B981', disabled: moneyError,
        value: moneyValue(money.booked.total),
        sub: moneySub(`${plural(money.booked.items.length, 'job', 'jobs')} · not invoiced`),
      },
      {
        key: 'outstanding', label: 'Outstanding', color: '#dc2626', disabled: moneyError,
        value: moneyValue(money.outstanding.total),
        sub: moneySub(`${plural(money.outstanding.items.length, 'invoice', 'invoices')} · ${
          overdueCount === 0 ? 'none' : overdueCount === money.outstanding.items.length ? 'all' : overdueCount} past due`),
      },
      {
        key: 'crew', label: 'Owed to Crew', color: '#F59E0B',
        value: crewLoadingState ? '...' : !crewReady ? '—' : fmtC(money.crew.total),
        sub: crewLoadingState || !crewReady ? '' : `${plural(money.crew.people.length, 'person', 'people')} · work done${
          money.crew.upcomingTotal > 0 ? ` · +${fmtCCompact(money.crew.upcomingTotal)} coming` : ''}`,
        disabled: !crewReady,
      },
    ] : []),
  ];
  const openBox = breakdown && statBoxes.find((b) => b.key === breakdown);

  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase();
  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <Box data-track-area="home">
      {/* Row 1: Date + Time Header + (admin) Plate picker */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>
            {dateStr}
          </Typography>
          <Typography variant="body2" sx={{ ...monoSx, color: 'text.secondary', fontSize: '0.8rem' }}>
            {timeStr}
          </Typography>
        </Box>
        {isAdmin && (plateUserId || !isHidden('home.platePicker')) && (
          <span data-focus="home.platePicker" style={{ display: 'contents' }}>
            <UserPlatePicker value={plateUserId} onChange={setPlateUserId} />
          </span>
        )}
      </Box>

      {alerts.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {alerts.map((a, i) => {
            const pill = (
              <Typography key={i} data-focus={a.focus} data-track={a.link ? 'home.alertPill' : undefined} variant="caption" sx={{ ...monoSx, fontSize: '0.7rem', color: a.color, bgcolor: `${a.color}10`, border: `1px solid ${a.color}25`, borderRadius: 1, px: { xs: 1.5, md: 1 }, py: { xs: 0.75, md: 0.25 }, cursor: a.link ? 'pointer' : 'default' }}
                onClick={a.link ? () => navigate(a.link) : undefined}>
                {a.msg}
              </Typography>
            );
            return pill;
          })}
        </Box>
      )}

      {/* The viewer's own to-dos, on every Home (an admin's plate view included). */}
      <HomeTodosCard />

      {/* Per-user view (admin selected a user) replaces the dashboard rows;
          Weather + Flight Resources still render below it. */}
      {plateUserId ? (
      <>
      <UserPlateView userId={plateUserId} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <WeatherWidget />
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>Flight Resources</Typography>
            <Grid container spacing={1}>
              {[
                { label: 'TFR Checker', url: 'https://tfr.faa.gov/tfr2/list.html', icon: '🚫' },
                { label: '1800WxBrief', url: 'https://www.1800wxbrief.com/', icon: '🌤' },
                { label: 'SkyVector', url: 'https://skyvector.com/', icon: '🗺' },
                { label: 'B4UFLY', url: 'https://www.faa.gov/uas/getting_started/b4ufly', icon: '✈' },
              ].map((link) => (
                <Grid key={link.url} size={6}>
                  <Button variant="outlined" size="small" fullWidth href={link.url} target="_blank" rel="noopener noreferrer" startIcon={<span>{link.icon}</span>} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.75rem' }}>
                    {link.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      </>
      ) : (
      <>
      {/* Row 2: Weather (left) + Meme of the Day (right). The meme renders nothing
          when it can't load; :empty then drops its column so weather spans the row. */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 2 }}>
        <Box sx={{ flex: { md: '7 1 0' }, minWidth: 0, '& > .MuiPaper-root': { height: '100%' } }}>
          <WeatherWidget />
        </Box>
        <Box sx={{ flex: { md: '5 1 0' }, minWidth: 0, '&:empty': { display: 'none' }, '& > .MuiPaper-root': { height: '100%', marginBottom: 0 } }}>
          <MemeOfTheDay />
        </Box>
      </Box>

      {/* Row 3: Stat Boxes */}
      <Grid container spacing={1} sx={{ mb: 2 }}>
        {statBoxes.map((stat) => {
          const onClick = stat.link ? () => navigate(stat.link)
            : stat.key && !isLoading && !stat.disabled ? () => setBreakdown(stat.key) : undefined;
          return (
            <Grid key={stat.label} size={{ xs: 6, sm: true }}>
              <Paper
                onClick={onClick}
                role={onClick ? 'button' : undefined}
                data-track={stat.key ? `home.stat.${stat.key}` : undefined}
                tabIndex={onClick ? 0 : undefined}
                onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
                aria-haspopup={stat.key ? 'dialog' : undefined}
                sx={{ p: 1.5, height: '100%', textAlign: 'center', ...(onClick ? { cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } } : {}) }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.6rem', textTransform: 'uppercase' }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.6rem', lineHeight: 1.2, color: stat.color }}>
                  {stat.value}
                </Typography>
                {stat.sub && (
                  <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.58rem', display: 'block', lineHeight: 1.3 }}>
                    {stat.sub}
                  </Typography>
                )}
              </Paper>
            </Grid>
          );
        })}
      </Grid>
      {openBox && (
        <MoneyBreakdownDialog
          kind={openBox.key}
          label={openBox.label}
          color={openBox.color}
          money={money}
          onClose={() => setBreakdown(null)}
        />
      )}

      {/* Row 4: Today's Shoots + Upcoming Shoots + Pipeline (compact) */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Today's Shoots</Typography>
            {todayJobs.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>No shoots today</Typography>
            ) : (
              todayJobs.map((j, i) => (
                <Box key={j.id}>
                  <Box
                    onClick={() => navigateToJob(navigate, j.id)}
                    sx={{ cursor: 'pointer', py: 1.5, '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Chip size="small" label={j.status} sx={{ fontSize: '0.6rem', height: 20, bgcolor: STATUS_COLORS[j.status] || '#666', color: '#fff' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{j.title || j.clientName || 'Untitled'}</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ ...monoSx, fontSize: '0.7rem' }}>
                      {j.clientName}{j.location ? ` · ${j.location}` : ''}
                    </Typography>
                    {isAdmin && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.2rem', color: '#13B5EA' }}>
                          {fmtC(parseFloat(j.revenue) || 0)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  {i < todayJobs.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="overline" color="text.secondary">Upcoming Shoots</Typography>
              <Link component={RouterLink} to="/calendar" underline="hover" variant="caption">Calendar</Link>
            </Box>
            {upcomingShoots.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>No shoots in the next 7 days</Typography>
            ) : (
              upcomingShoots.map((job, i) => (
                <Box key={job.id}>
                  <Box onClick={() => navigateToJob(navigate, job.id)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'primary.main', opacity: 0.15, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 700, lineHeight: 1, fontSize: '0.75rem' }}>
                        {new Date(job.shootDate + 'T00:00:00').getDate()}
                      </Typography>
                      <Typography sx={{ fontSize: '0.55rem', color: 'primary.main', textTransform: 'uppercase' }}>
                        {new Date(job.shootDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short' })}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem' }}>{job.title || job.clientName || 'Untitled'}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{job.location || job.jobType || '—'}</Typography>
                    </Box>
                    <Chip size="small" label={job.status} sx={{ fontSize: '0.6rem', height: 18, bgcolor: STATUS_COLORS[job.status] || '#666', color: '#fff' }} />
                  </Box>
                  {i < upcomingShoots.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Pipeline</Typography>
            {pipelineData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No jobs</Typography>
            ) : (
              pipelineData.map((d) => (
                <Box key={d.status} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: '2px' }}>
                  <Typography sx={{ ...monoSx, fontSize: '0.62rem', color: STATUS_COLORS[d.status] || '#666', width: 62, textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap' }}>
                    {STAGE_LABELS[d.status] || d.status}
                  </Typography>
                  <Box sx={{ flex: 1, height: 5, bgcolor: 'action.hover', borderRadius: '3px', overflow: 'hidden' }}>
                    <Box sx={{ width: `${d.pct}%`, height: '100%', bgcolor: STATUS_COLORS[d.status] || '#666', borderRadius: '3px', transition: 'width 0.3s' }} />
                  </Box>
                  {isAdmin && (
                    <Typography sx={{ ...monoSx, fontSize: '0.62rem', color: STATUS_COLORS[d.status] || 'text.disabled', width: 52, textAlign: 'right', flexShrink: 0 }}>
                      {fmtCCompact(d.rev)}
                    </Typography>
                  )}
                  <Typography sx={{ ...monoSx, fontSize: '0.6rem', color: 'text.disabled', width: 14, textAlign: 'right', flexShrink: 0 }}>
                    {d.count}
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row 5: Action Required + Flight Resources */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Action Required</Typography>
            {actionItems.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ ...monoSx, fontSize: '0.75rem' }}>All clear</Typography>
            ) : (
              actionItems.map((item, i) => (
                <Box
                  key={i}
                  onClick={() => item.jobId ? navigateToJob(navigate, item.jobId) : item.link ? navigate(item.link) : null}
                  sx={{ py: { xs: 1, md: 0.5 }, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderRadius: 1, px: 1 }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                    {item.icon} <span>{item.label}</span>{' '}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>— {item.sub}</Typography>
                  </Typography>
                </Box>
              ))
            )}
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>Flight Resources</Typography>
            <Grid container spacing={1}>
              {[
                { label: 'TFR Checker', url: 'https://tfr.faa.gov/tfr2/list.html', icon: '🚫' },
                { label: '1800WxBrief', url: 'https://www.1800wxbrief.com/', icon: '🌤' },
                { label: 'SkyVector', url: 'https://skyvector.com/', icon: '🗺' },
                { label: 'B4UFLY', url: 'https://www.faa.gov/uas/getting_started/b4ufly', icon: '✈' },
              ].map((link) => (
                <Grid key={link.url} size={6}>
                  <Button variant="outlined" size="small" fullWidth href={link.url} target="_blank" rel="noopener noreferrer" startIcon={<span>{link.icon}</span>} sx={{ justifyContent: 'flex-start', textTransform: 'none', fontSize: '0.75rem' }}>
                    {link.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      </>
      )}
    </Box>
  );
}
